import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import axios from "axios";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import FUNCTIONS_URLS from '../../../../Config/functions';
import { adminAuthHeaders } from '../../../../Config/adminAuthHeaders';


const COLLECTION_LABELS = {
  ledgers: "Ledgers",
  monthly_closings: "Monthly Closings",
  monthly_grpahs: "Monthly Graphs",
  notifications: "Notifications",
  transection_requests: "Transaction Requests",
};

// Resizable Table Cell Component
const ResizableTableCell = ({ children, onWidthChange, minWidth = 100, initialWidth = 150, ...props }) => {
  const [width, setWidth] = React.useState(initialWidth);
  const [isResizing, setIsResizing] = React.useState(false);
  const cellRef = React.useRef(null);

  React.useEffect(() => {
    setWidth(initialWidth);
  }, [initialWidth]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    const startX = e.pageX;
    const startWidth = width;

    const handleMouseMove = (e) => {
      const newWidth = Math.max(minWidth, startWidth + (e.pageX - startX));
      setWidth(newWidth);
      if (onWidthChange) onWidthChange(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <TableCell
      {...props}
      ref={cellRef}
      sx={{
        ...props.sx,
        width: `${width}px`,
        minWidth: `${width}px`,
        maxWidth: `${width}px`,
        position: "relative",
        paddingRight: "8px !important",
      }}
    >
      <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {children}
      </Box>
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          width: "4px",
          cursor: "col-resize",
          backgroundColor: isResizing ? "#2af598" : "transparent",
          "&:hover": {
            backgroundColor: "#2af598",
          },
          zIndex: 1,
        }}
      />
    </TableCell>
  );
};

const CollectionCleanUpTool = () => {
  const [selectedCollection, setSelectedCollection] = useState("ledgers");
  const [loading, setLoading] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [usersMap, setUsersMap] = useState(new Map());
  const [selectedDocs, setSelectedDocs] = useState({ tableB: [], tableC: [], tableD: [] });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [columnWidths, setColumnWidths] = useState({});
  const [dateRangeStart, setDateRangeStart] = useState("");
  const [dateRangeEnd, setDateRangeEnd] = useState("");
  const [missingUserIds, setMissingUserIds] = useState(new Set()); // Track missing user IDs

  useEffect(() => {
    loadUsers();
  }, []);

  // Extract all user IDs from analysis data and ensure they're loaded
  useEffect(() => {
    if (analysisData && usersMap.size > 0) {
      const allUserIds = new Set();
      
      // Collect all user_ids from all tables
      [...(analysisData.tableA || []), 
       ...(analysisData.tableB || []), 
       ...(analysisData.tableC || []),
       ...(analysisData.tableD || [])].forEach(item => {
        const doc = item.prodDoc || item.rowyDoc;
        // Try multiple possible user ID fields
        const userId = doc?.data?.user_id || doc?.data?.userId || doc?.data?.user?.id || null;
        if (userId && typeof userId === 'string' && userId.trim() !== '') {
          allUserIds.add(userId.trim());
        }
      });
      
      console.log(`📊 Found ${allUserIds.size} unique user IDs in analysis data`);
      
      // Check for specific users mentioned by user
      const targetUser1 = 'igh16uiOxOhd2DzcDeKOmUHTbcp2';
      const targetUser2 = 'xcsoLsBmp3NO5SgL0akEuBqQWCN2';
      
      if (allUserIds.has(targetUser1)) {
        console.log(`✅ Found target user 1 (${targetUser1}) in analysis data`);
        console.log(`   In usersMap: ${usersMap.has(targetUser1)}, Value: ${usersMap.get(targetUser1)}`);
      }
      if (allUserIds.has(targetUser2)) {
        console.log(`✅ Found target user 2 (${targetUser2}) in analysis data`);
        console.log(`   In usersMap: ${usersMap.has(targetUser2)}, Value: ${usersMap.get(targetUser2)}`);
      }
      
      // Check which users are missing from map
      const missing = Array.from(allUserIds).filter(userId => !usersMap.has(userId) || usersMap.get(userId) === userId);
      
      if (missing.length > 0) {
        console.log(`🔍 Found ${missing.length} missing/unknown user IDs. Fetching...`);
        // Fetch missing users
        fetchMissingUsers(missing);
      } else {
        console.log(`✅ All user IDs found in map`);
      }
    }
  }, [analysisData]); // Only trigger when analysisData changes (not usersMap.size to avoid loops)
  
  const fetchMissingUsers = async (userIds) => {
    if (!userIds || userIds.length === 0) return;
    
    console.log(`🔍 Fetching ${userIds.length} missing users from database...`);
    const newMap = new Map(usersMap);
    let fetchedCount = 0;
    let notFoundCount = 0;
    
    // Limit to first 100 to avoid too many requests
    const userIdsToFetch = userIds.slice(0, 100);
    
    // Special handling for users mentioned by user
    const targetUsers = ['igh16uiOxOhd2DzcDeKOmUHTbcp2', 'xcsoLsBmp3NO5SgL0akEuBqQWCN2'];
    const targetUsersToFetch = userIdsToFetch.filter(id => targetUsers.includes(id));
    
    // Fetch target users first (priority)
    for (const userId of targetUsersToFetch) {
      try {
        const userDocRef = doc(DATABASE, "users", userId);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const userName = userData.full_name || userData.email || userId;
          newMap.set(userId, userName);
          fetchedCount++;
          console.log(`   ✅ [TARGET] Fetched user ${userId}: ${userName}`, userData);
        } else {
          console.warn(`   ⚠️ [TARGET] User document ${userId} does not exist in users collection`);
          // Try searching by doc_id
          const usersSnapshot = await getDocs(collection(DATABASE, "users"));
          let found = false;
          usersSnapshot.docs.forEach(d => {
            const data = d.data();
            if (d.id === userId || data.doc_id === userId) {
              const userName = data.full_name || data.email || d.id;
              newMap.set(userId, userName);
              newMap.set(d.id, userName); // Also add by document ID
              fetchedCount++;
              found = true;
              console.log(`   ✅ [TARGET] Found user ${userId} by doc_id/search (document ID: ${d.id}): ${userName}`, { id: d.id, doc_id: data.doc_id, name: data.full_name, email: data.email });
            }
          });
          if (!found) {
            notFoundCount++;
            newMap.set(userId, userId); // Keep ID for display
            console.error(`   ❌ [TARGET] User ${userId} NOT FOUND in database at all`);
          }
        }
      } catch (error) {
        console.error(`   ❌ [TARGET] Error fetching user ${userId}:`, error);
        newMap.set(userId, userId);
        notFoundCount++;
      }
    }
    
    // Fetch remaining users
    const remainingUsers = userIdsToFetch.filter(id => !targetUsers.includes(id));
    for (const userId of remainingUsers) {
      try {
        const userDocRef = doc(DATABASE, "users", userId);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const userName = userData.full_name || userData.email || userId;
          newMap.set(userId, userName);
          fetchedCount++;
        } else {
          // Check if maybe it's a doc_id reference instead
          const usersSnapshot = await getDocs(collection(DATABASE, "users"));
          let found = false;
          usersSnapshot.docs.forEach(d => {
            if (d.data().doc_id === userId) {
              const userName = d.data().full_name || d.data().email || d.id;
              newMap.set(userId, userName);
              newMap.set(d.id, userName);
              fetchedCount++;
              found = true;
            }
          });
          if (!found) {
            notFoundCount++;
            newMap.set(userId, userId);
          }
        }
      } catch (error) {
        console.error(`   ❌ Error fetching user ${userId}:`, error);
        newMap.set(userId, userId);
        notFoundCount++;
      }
    }
    
    if (fetchedCount > 0) {
      setUsersMap(newMap);
      console.log(`✅ Successfully fetched ${fetchedCount} missing users (${targetUsersToFetch.length} target users)`);
    }
    
    if (notFoundCount > 0) {
      console.warn(`⚠️ ${notFoundCount} users could not be found in database`);
    }
    
    if (userIds.length > 100) {
      console.warn(`⚠️ Only fetched first 100 missing users. ${userIds.length - 100} remaining.`);
    }
  };

  const loadUsers = async () => {
    try {
      console.log('🔄 Loading all users from database...');
      const snapshot = await getDocs(collection(DATABASE, "users"));
      const map = new Map();
      
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        const userName = data.full_name || data.email || doc.id;
        
        // CRITICAL: Index by document ID (this is how user_id references users in documents)
        map.set(doc.id, userName);
        
        // Also index by doc_id if it exists (some older documents use doc_id to reference users)
        if (data.doc_id && data.doc_id !== doc.id) {
          map.set(data.doc_id, userName);
        }
        
        // Also index by email as fallback (case-insensitive)
        if (data.email && typeof data.email === 'string') {
          map.set(data.email.toLowerCase(), userName);
        }
      });
      
      setUsersMap(map);
      console.log(`✅ Loaded ${snapshot.size} users into map`);
      console.log(`   Map size: ${map.size} entries`);
      
      // Verify specific users mentioned by user for debugging
      const testUser1Id = 'igh16uiOxOhd2DzcDeKOmUHTbcp2';
      const testUser2Id = 'xcsoLsBmp3NO5SgL0akEuBqQWCN2';
      
      const testUser1 = map.get(testUser1Id);
      const testUser2 = map.get(testUser2Id);
      
      if (testUser1) {
        console.log(`✅ Found user ${testUser1Id}: ${testUser1}`);
      } else {
        console.warn(`⚠️ User ${testUser1Id} NOT FOUND in users map`);
        console.warn(`   Map has ${map.size} entries. Checking if user exists in database...`);
        // Try to find if user exists with different ID
        snapshot.docs.forEach(d => {
          if (d.id === testUser1Id || d.data().doc_id === testUser1Id || d.data().email?.includes('luca') || d.data().full_name?.toLowerCase().includes('governo')) {
            console.log(`   Found potential match: ID=${d.id}, doc_id=${d.data().doc_id}, name=${d.data().full_name}, email=${d.data().email}`);
          }
        });
      }
      
      if (testUser2) {
        console.log(`✅ Found user ${testUser2Id}: ${testUser2}`);
      } else {
        console.warn(`⚠️ User ${testUser2Id} NOT FOUND in users map`);
        console.warn(`   Checking if user exists in database...`);
        snapshot.docs.forEach(d => {
          if (d.id === testUser2Id || d.data().doc_id === testUser2Id || d.data().email?.includes('francesco') || d.data().full_name?.toLowerCase().includes('bono')) {
            console.log(`   Found potential match: ID=${d.id}, doc_id=${d.data().doc_id}, name=${d.data().full_name}, email=${d.data().email}`);
          }
        });
      }
    } catch (error) {
      console.error("❌ Error loading users:", error);
    }
  };

  const getUserName = (userId) => {
    if (!userId || userId === 'undefined' || userId === 'null') {
      return "N/A";
    }
    
    // Normalize userId (trim whitespace, convert to string)
    const normalizedUserId = String(userId).trim();
    if (!normalizedUserId) return "N/A";
    
    // Try direct lookup by userId
    let userName = usersMap.get(normalizedUserId);
    if (userName && userName !== normalizedUserId) {
      return userName;
    }
    
    // If not found, mark as missing for potential reload
    if (!usersMap.has(normalizedUserId)) {
      setMissingUserIds(prev => new Set([...prev, normalizedUserId]));
    }
    
    // Return the userId itself for debugging - this will show in UI if user is missing
    return normalizedUserId || "N/A";
  };
  
  // Fetch missing users on demand
  const fetchMissingUser = async (userId) => {
    if (!userId) return null;
    
    try {
      const userDoc = await getDoc(doc(DATABASE, "users", userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const foundName = userData.full_name || userData.email || userId;
        // Update map for future lookups
        setUsersMap(prev => {
          const newMap = new Map(prev);
          newMap.set(userId, foundName);
          return newMap;
        });
        return foundName;
      }
    } catch (error) {
      console.error(`Error fetching missing user ${userId}:`, error);
    }
    return null;
  };

  const analyzeCollection = async () => {
    setLoading(true);
    try {
      // First, ensure users are loaded
      await loadUsers();
      
      const functionUrl = FUNCTIONS_URLS.AnalyzeCollectionCleanUp;
      const payload = {
        collectionName: selectedCollection,
      };
      
      // Add date range if provided
      if (dateRangeStart && dateRangeEnd) {
        payload.startDate = dateRangeStart;
        payload.endDate = dateRangeEnd;
      }
      
      const response = await axios.post(functionUrl, payload);

      setAnalysisData(response.data);
      setSelectedDocs({ tableB: [], tableC: [], tableD: [] });
      
      // CRITICAL: Reload users after analysis to ensure we have all referenced users
      // Extract all user IDs from analysis data first
      const allUserIdsFromAnalysis = new Set();
      [...(response.data.tableA || []), 
       ...(response.data.tableB || []), 
       ...(response.data.tableC || []),
       ...(response.data.tableD || [])].forEach(item => {
        const doc = item.prodDoc || item.rowyDoc;
        const userId = doc?.data?.user_id;
        if (userId && typeof userId === 'string' && userId.trim() !== '') {
          allUserIdsFromAnalysis.add(userId.trim());
        }
      });
      
      console.log(`📊 Analysis complete. Found ${allUserIdsFromAnalysis.size} unique user IDs. Reloading users...`);
      await loadUsers();
      
      // Check for missing users and fetch them
      const currentMapSize = usersMap.size;
      const missing = Array.from(allUserIdsFromAnalysis).filter(userId => !usersMap.has(userId));
      if (missing.length > 0) {
        console.log(`🔍 Found ${missing.length} missing users after reload. Fetching...`);
        await fetchMissingUsers(missing);
      }
      
    } catch (error) {
      console.error("Error analyzing collection:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.error || error.message,
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Filter data by date range for display
  const filterByDateRange = (data, startDate, endDate) => {
    if (!data || !startDate || !endDate) return data;
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999); // Include entire end date
    
    return data.filter(item => {
      // Handle tableD differently (uses prodDoc instead of rowyDoc)
      const docData = item.prodDoc?.data || item.rowyDoc?.data || {};
      let docDate = null;
      
      // Try different date fields
      if (docData.date) {
        if (typeof docData.date === 'string') {
          docDate = new Date(docData.date);
        } else if (docData.date.toDate) {
          docDate = docData.date.toDate();
        } else if (docData.date.toMillis) {
          docDate = new Date(docData.date.toMillis());
        }
      } else if (docData.created_at) {
        if (typeof docData.created_at === 'string') {
          docDate = new Date(docData.created_at);
        } else if (docData.created_at.toDate) {
          docDate = docData.created_at.toDate();
        } else if (docData.created_at.toMillis) {
          docDate = new Date(docData.created_at.toMillis());
        }
      } else if (docData._migrated_at) {
        if (typeof docData._migrated_at === 'string') {
          docDate = new Date(docData._migrated_at);
        } else if (docData._migrated_at.toDate) {
          docDate = docData._migrated_at.toDate();
        } else if (docData._migrated_at.toMillis) {
          docDate = new Date(docData._migrated_at.toMillis());
        }
      }
      
      if (!docDate || isNaN(docDate.getTime())) return false;
      
      return docDate >= start && docDate <= end;
    });
  };
  
  // Calculate range summary
  const getRangeSummary = () => {
    if (!analysisData || !dateRangeStart || !dateRangeEnd) return null;
    
    const filteredA = filterByDateRange(analysisData.tableA || [], dateRangeStart, dateRangeEnd);
    const filteredB = filterByDateRange(analysisData.tableB || [], dateRangeStart, dateRangeEnd);
    const filteredC = filterByDateRange(analysisData.tableC || [], dateRangeStart, dateRangeEnd);
    const filteredD = filterByDateRange(analysisData.tableD || [], dateRangeStart, dateRangeEnd);
    
    // Count production and rowy totals in range
    let productionInRange = 0;
    let rowyInRange = 0;
    
    // Count production docs in range
    if (analysisData.productionDocs && Array.isArray(analysisData.productionDocs)) {
      productionInRange = analysisData.productionDocs.filter(doc => {
        const start = new Date(dateRangeStart);
        const end = new Date(dateRangeEnd);
        end.setHours(23, 59, 59, 999);
        
        let docDate = null;
        if (doc.date) {
          docDate = typeof doc.date === 'string' ? new Date(doc.date) : null;
        } else if (doc.created_at) {
          docDate = typeof doc.created_at === 'string' ? new Date(doc.created_at) : null;
        } else if (doc._migrated_at) {
          docDate = typeof doc._migrated_at === 'string' ? new Date(doc._migrated_at) : null;
        }
        
        if (!docDate || isNaN(docDate.getTime())) return false;
        return docDate >= start && docDate <= end;
      }).length;
    }
    
    // Count rowy docs in range (sum of all filtered tables)
    rowyInRange = filteredA.length + filteredB.length + filteredC.length;
    
    return {
      productionTotal: productionInRange,
      rowyTotal: rowyInRange,
      tableA: filteredA.length,
      tableB: filteredB.length,
      tableC: filteredC.length,
      tableD: filteredD.length,
    };
  };

  const handleSelectDoc = (table, docId) => {
    setSelectedDocs((prev) => {
      const newSelected = { ...prev };
      if (newSelected[table].includes(docId)) {
        newSelected[table] = newSelected[table].filter((id) => id !== docId);
      } else {
        newSelected[table] = [...newSelected[table], docId];
      }
      return newSelected;
    });
  };

  const handleSelectAll = (table, docIds) => {
    setSelectedDocs((prev) => {
      const allSelected = docIds.every((id) => prev[table].includes(id));
      return {
        ...prev,
        [table]: allSelected ? [] : [...docIds],
      };
    });
  };

  const exportToCSV = (tableData, tableName, title) => {
    if (!tableData || tableData.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Data",
        text: "No data to export",
      });
      return;
    }

    // Get all unique field keys
    const allFieldKeys = new Set();
    tableData.forEach((item) => {
      const docData = tableName === "tableD" ? item.prodDoc.data : item.rowyDoc.data;
      if (docData) {
        Object.keys(docData).forEach((key) => allFieldKeys.add(key));
      }
    });
    const sortedFieldKeys = Array.from(allFieldKeys).sort();

    // Create CSV headers
    const headers = ["User Name", "Document ID", ...sortedFieldKeys, "Category", "Reason"];
    const rows = [headers];

    // Add data rows
    tableData.forEach((item) => {
      const isTableD = tableName === "tableD";
      const doc = isTableD ? item.prodDoc : item.rowyDoc;
      const userId = doc.data?.user_id;
      const userName = getUserName(userId);
      const docData = doc.data || {};
      
      const row = [
        userName,
        doc.id,
        ...sortedFieldKeys.map((key) => {
          const value = docData[key];
          if (value === null || value === undefined) return "";
          if (typeof value === "object") return JSON.stringify(value);
          return String(value);
        }),
        tableName === "tableA" ? "Identical" : tableName === "tableB" ? "Possible Duplicate" : tableName === "tableC" ? "Rowy Only" : "Missing in Rowy",
        item.reason || ""
      ];
      rows.push(row);
    });

    // Convert to CSV string
    const csvContent = rows
      .map((row) =>
        row
          .map((cell) => {
            const cellStr = String(cell || "");
            // Escape quotes and wrap in quotes if contains comma, newline, or quote
            if (cellStr.includes(",") || cellStr.includes("\n") || cellStr.includes('"')) {
              return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
          })
          .join(",")
      )
      .join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${COLLECTION_LABELS[selectedCollection]}_${title.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteSelected = async () => {
    const totalSelected = selectedDocs.tableB.length + selectedDocs.tableC.length;
    if (totalSelected === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Selection",
        text: "Please select documents to delete",
      });
      return;
    }

    const result = await Swal.fire({
      icon: "warning",
      title: "Confirm Deletion",
      html: `Are you sure you want to delete <strong>${totalSelected}</strong> document(s) from Rowy?<br/><br/>This action is <strong>IRREVERSIBLE</strong>!`,
      showCancelButton: true,
      confirmButtonColor: "#f52a2d",
      cancelButtonColor: "#2af598",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    setDeleting(true);
    try {
      const allDocIds = [...selectedDocs.tableB, ...selectedDocs.tableC];
      const functionUrl = FUNCTIONS_URLS.DeleteRowyDocuments;
      const response = await axios.post(functionUrl, {
        collectionName: selectedCollection,
        docIds: allDocIds,
      }, { headers: await adminAuthHeaders() });

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: `Successfully deleted ${response.data.deleted} document(s)`,
      });

      setSelectedDocs({ tableB: [], tableC: [], tableD: [] });
      await analyzeCollection(); // Refresh data
    } catch (error) {
      console.error("Error deleting documents:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.error || error.message,
      });
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleImportSelected = async () => {
    const totalSelected = selectedDocs.tableD.length;
    if (totalSelected === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Selection",
        text: "Please select documents to import",
      });
      return;
    }

    const result = await Swal.fire({
      icon: "question",
      title: "Confirm Import",
      html: `Are you sure you want to import <strong>${totalSelected}</strong> document(s) from Production to Rowy?<br/><br/>This will add these documents to Rowy with migration flags.`,
      showCancelButton: true,
      confirmButtonColor: "#2af598",
      cancelButtonColor: "#666",
      confirmButtonText: "Yes, Import",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    setImporting(true);
    try {
      const functionUrl = FUNCTIONS_URLS.ImportSelectedDocuments;
      const response = await axios.post(functionUrl, {
        collectionName: selectedCollection,
        docIds: selectedDocs.tableD,
      });

      Swal.fire({
        icon: "success",
        title: "Imported",
        text: `Successfully imported ${response.data.imported} document(s) from Production`,
      });

      setSelectedDocs({ tableB: [], tableC: [], tableD: [] });
      await analyzeCollection(); // Refresh data
    } catch (error) {
      console.error("Error importing documents:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.error || error.message,
      });
    } finally {
      setImporting(false);
    }
  };

  const renderTable = (tableData, tableName, title) => {
    if (!tableData || tableData.length === 0) return null;

    // For tableD, use prodDoc.id instead of rowyDoc.id
    const docIds = tableName === "tableD" 
      ? tableData.map((item) => item.prodDoc.id)
      : tableData.map((item) => item.rowyDoc.id);
    const selectedArray = selectedDocs[tableName] || [];
    const allSelected = docIds.length > 0 && docIds.every((id) => selectedArray.includes(id));

    // Get all unique field keys from all documents and sort them for consistent ordering
    const allFieldKeys = new Set();
    tableData.forEach((item) => {
      const docData = tableName === "tableD" ? item.prodDoc.data : item.rowyDoc.data;
      if (docData) {
        Object.keys(docData).forEach((key) => allFieldKeys.add(key));
      }
    });
    // Sort keys for consistent display order
    const sortedFieldKeys = Array.from(allFieldKeys).sort();

    return (
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {title} ({tableData.length})
            </Typography>
            {tableName !== "tableA" && (
              <Chip
                label={`${selectedArray.length} selected`}
                size="small"
                color={selectedArray.length > 0 ? "error" : "default"}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <Button
              size="small"
              onClick={() => exportToCSV(tableData, tableName, title)}
              variant="outlined"
              startIcon={<DownloadIcon />}
              sx={{ color: "#2af598", borderColor: "#2af598" }}
            >
              Export to CSV
            </Button>
            {tableName !== "tableA" && (
              <>
                <Button
                  size="small"
                  onClick={() => handleSelectAll(tableName, docIds)}
                  variant="outlined"
                >
                  {allSelected ? "Deselect All" : "Select All"}
                </Button>
                {tableName === "tableD" && selectedArray.length > 0 && (
                  <Button
                    size="small"
                    onClick={handleImportSelected}
                    variant="contained"
                    color="success"
                    disabled={importing}
                    startIcon={importing ? <CircularProgress size={16} /> : <RefreshIcon />}
                    sx={{ backgroundColor: "#2af598", color: "#000" }}
                  >
                    {importing ? "Importing..." : `Import Selected (${selectedArray.length})`}
                  </Button>
                )}
                {(tableName === "tableB" || tableName === "tableC") && selectedArray.length > 0 && (
                  <Button
                    size="small"
                    onClick={handleDeleteSelected}
                    variant="contained"
                    color="error"
                    disabled={deleting}
                    startIcon={<DeleteIcon />}
                  >
                    Delete Selected ({selectedArray.length})
                  </Button>
                )}
              </>
            )}
          </Box>
          <TableContainer component={Paper} sx={{ maxHeight: "600px", overflow: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {(tableName !== "tableA" || tableName === "tableD") && (
                    <TableCell padding="checkbox" sx={{ fontWeight: "bold", backgroundColor: "#2a2a2a", position: "sticky", left: 0, zIndex: 10, width: "50px", minWidth: "50px" }}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={selectedArray.length > 0 && !allSelected}
                        onChange={() => handleSelectAll(tableName, docIds)}
                      />
                    </TableCell>
                  )}
                  <ResizableTableCell 
                    sx={{ fontWeight: "bold", backgroundColor: "#2a2a2a" }}
                    initialWidth={columnWidths[`${tableName}_userName`] || 150}
                    onWidthChange={(w) => setColumnWidths(prev => ({ ...prev, [`${tableName}_userName`]: w }))}
                  >
                    User Name
                  </ResizableTableCell>
                  <ResizableTableCell 
                    sx={{ fontWeight: "bold", backgroundColor: "#2a2a2a", fontFamily: "monospace" }}
                    initialWidth={columnWidths[`${tableName}_docId`] || 200}
                    onWidthChange={(w) => setColumnWidths(prev => ({ ...prev, [`${tableName}_docId`]: w }))}
                  >
                    Document ID
                  </ResizableTableCell>
                  {sortedFieldKeys.map((key) => (
                    <ResizableTableCell 
                      key={key} 
                      sx={{ fontWeight: "bold", backgroundColor: "#2a2a2a" }}
                      initialWidth={columnWidths[`${tableName}_${key}`] || 150}
                      onWidthChange={(w) => setColumnWidths(prev => ({ ...prev, [`${tableName}_${key}`]: w }))}
                    >
                      {key}
                    </ResizableTableCell>
                  ))}
                  <ResizableTableCell 
                    sx={{ fontWeight: "bold", backgroundColor: "#2a2a2a" }}
                    initialWidth={columnWidths[`${tableName}_verdict`] || 150}
                    onWidthChange={(w) => setColumnWidths(prev => ({ ...prev, [`${tableName}_verdict`]: w }))}
                  >
                    Verdict
                  </ResizableTableCell>
                  {tableName !== "tableA" && (
                    <TableCell sx={{ fontWeight: "bold", backgroundColor: "#2a2a2a", width: "80px", minWidth: "80px" }}>
                      Action
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.map((item) => {
                  // Handle tableD differently (uses prodDoc instead of rowyDoc)
                  const isTableD = tableName === "tableD";
                  const doc = isTableD ? item.prodDoc : item.rowyDoc;
                  const docId = doc.id;
                  // Extract user_id - can be in different fields depending on collection
                  // CRITICAL: Check all possible user ID fields
                  const userId = doc.data?.user_id || doc.data?.userId || doc.data?.user?.id || null;
                  const isSelected = selectedArray.includes(docId);
                  const docData = doc.data || {};
                  
                  // Debug: Log specific users mentioned by user
                  if (userId === 'igh16uiOxOhd2DzcDeKOmUHTbcp2' || userId === 'xcsoLsBmp3NO5SgL0akEuBqQWCN2') {
                    console.log(`🔍 Found target user ID ${userId} in document ${docId}:`, {
                      tableName,
                      userId,
                      foundInMap: usersMap.has(userId),
                      mapValue: usersMap.get(userId),
                      allDataKeys: Object.keys(docData),
                      user_id_field: doc.data?.user_id,
                      userId_field: doc.data?.userId,
                      doc_data: doc.data
                    });
                  }
                  
                  // Debug: Log if userId is found but not in map
                  if (userId && !usersMap.has(userId) && userId !== docId && typeof userId === 'string' && userId.length > 5) {
                    console.warn(`⚠️ User ID "${userId}" not found in usersMap for document ${docId} in ${tableName}`, {
                      availableKeys: Object.keys(docData),
                      docId,
                      tableName
                    });
                  }

                  return (
                    <TableRow key={docId} selected={isSelected}>
                      {(tableName !== "tableA" || tableName === "tableD") && (
                        <TableCell padding="checkbox" sx={{ position: "sticky", left: 0, backgroundColor: isSelected ? "rgba(42, 245, 152, 0.1)" : "inherit", zIndex: 5, width: "50px", minWidth: "50px" }}>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleSelectDoc(tableName, docId)}
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ 
                        fontWeight: "medium", 
                        width: `${columnWidths[`${tableName}_userName`] || 150}px`,
                        minWidth: `${columnWidths[`${tableName}_userName`] || 150}px`,
                        maxWidth: `${columnWidths[`${tableName}_userName`] || 150}px`
                      }}>
                        {(() => {
                          if (!userId) return "N/A";
                          const userName = getUserName(userId);
                          // If userName is same as userId, it means we couldn't find the user
                          // Show it but with warning styling
                          const isNotFound = userName === userId;
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: isNotFound ? '#ff9800' : 'inherit',
                                  fontStyle: isNotFound ? 'italic' : 'normal'
                                }}
                              >
                                {userName}
                              </Typography>
                              {isNotFound && (
                                <Tooltip title="User not found in database - showing ID">
                                  <InfoIcon sx={{ fontSize: 14, color: '#ff9800' }} />
                                </Tooltip>
                              )}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ 
                        fontFamily: "monospace", 
                        fontSize: "0.85rem", 
                        width: `${columnWidths[`${tableName}_docId`] || 200}px`,
                        minWidth: `${columnWidths[`${tableName}_docId`] || 200}px`,
                        maxWidth: `${columnWidths[`${tableName}_docId`] || 200}px`
                      }}>
                        {docId}
                      </TableCell>
                      {sortedFieldKeys.map((key) => {
                        const value = docData[key];
                        let displayValue = "";
                        if (value === null || value === undefined) {
                          displayValue = "";
                        } else if (typeof value === "object" && !Array.isArray(value)) {
                          displayValue = JSON.stringify(value);
                        } else if (Array.isArray(value)) {
                          displayValue = JSON.stringify(value);
                        } else {
                          displayValue = String(value);
                        }
                        const colWidth = columnWidths[`${tableName}_${key}`] || 150;
                        return (
                          <TableCell 
                            key={key} 
                            sx={{ 
                              fontSize: "0.9rem", 
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                              width: `${colWidth}px`,
                              minWidth: `${colWidth}px`,
                              maxWidth: `${colWidth}px`
                            }}
                            title={displayValue}
                          >
                            {displayValue}
                          </TableCell>
                        );
                      })}
                      <TableCell sx={{ 
                        width: `${columnWidths[`${tableName}_verdict`] || 150}px`,
                        minWidth: `${columnWidths[`${tableName}_verdict`] || 150}px`,
                        maxWidth: `${columnWidths[`${tableName}_verdict`] || 150}px`
                      }}>
                        <Tooltip
                          title={item.reason || "Identical document in both databases"}
                          componentsProps={{
                            tooltip: {
                              sx: { fontSize: "1rem", maxWidth: "400px" },
                            },
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <InfoIcon sx={{ fontSize: 18, cursor: "help" }} />
                            <Typography variant="caption">
                              {tableName === "tableA"
                                ? (item.reason && item.reason.includes('different IDs') 
                                    ? "Identical (Different IDs)" 
                                    : "Identical")
                                : tableName === "tableB"
                                ? "Possible Duplicate"
                                : tableName === "tableC"
                                ? "Rowy Only"
                                : "Missing in Rowy"}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      {(tableName === "tableB" || tableName === "tableC") && (
                        <TableCell sx={{ width: "80px", minWidth: "80px" }}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setSelectedDocs((prev) => ({
                                ...prev,
                                [tableName]: [docId],
                              }));
                              handleDeleteSelected();
                            }}
                            sx={{ verticalAlign: "middle" }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, color: "#fff", fontWeight: "bold" }}>
        Collection Clean Up Tool
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          <strong>Purpose:</strong> Clean up Rowy database before migration by identifying and removing duplicates and test data.
        </Typography>
        <Typography variant="body2">
          <strong>Table A:</strong> Identical documents in both databases (safe to keep). 
          Some may have different IDs but same business key (same financial data, migrated/recreated).
          <br />
          <strong>Table B:</strong> Possible duplicates in Rowy (review and delete if duplicate)
          <br />
          <strong>Table C:</strong> Rowy-only documents (likely test data, can be deleted)
          <br />
          <strong>Table D:</strong> Missing in Rowy (exists in Production, can be imported)
        </Typography>
        {analysisData?.summary?.idDiscrepancies > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>⚠️ ID Discrepancies Found:</strong> {analysisData.summary.idDiscrepancies} document(s) in Table A have the same business key (financial data) 
              but different document IDs between Production and Rowy. This typically happens when documents are migrated/recreated 
              and get new IDs. These are considered identical for migration purposes.
            </Typography>
          </Alert>
        )}
      </Alert>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
          <Typography variant="body1" sx={{ color: "#fff" }}>
            Select Collection:
          </Typography>
          <select
            value={selectedCollection}
            onChange={(e) => setSelectedCollection(e.target.value)}
            style={{
              padding: "8px 16px",
              fontSize: "1rem",
              backgroundColor: "#2a2a2a",
              color: "#fff",
              border: "1px solid #444",
              borderRadius: "4px",
            }}
          >
            {Object.keys(COLLECTION_LABELS).map((key) => (
              <option key={key} value={key}>
                {COLLECTION_LABELS[key]}
              </option>
            ))}
          </select>
          <Button
            variant="contained"
            onClick={analyzeCollection}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            sx={{ backgroundColor: "#2af598", color: "#000" }}
          >
            {loading ? "Analyzing..." : "Analyze Collection"}
          </Button>
        </Box>
        
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", p: 2, backgroundColor: "#1a1a1a", borderRadius: 1 }}>
          <Typography variant="body2" sx={{ color: "#fff", fontWeight: "bold" }}>
            Optional Date Range Filter:
          </Typography>
          <TextField
            type="date"
            label="Start Date"
            value={dateRangeStart}
            onChange={(e) => setDateRangeStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "#fff",
                "& fieldset": { borderColor: "#444" },
                "&:hover fieldset": { borderColor: "#2af598" },
                "&.Mui-focused fieldset": { borderColor: "#2af598" },
              },
              "& .MuiInputLabel-root": { color: "#aaa" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#2af598" },
            }}
            size="small"
          />
          <TextField
            type="date"
            label="End Date"
            value={dateRangeEnd}
            onChange={(e) => setDateRangeEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "#fff",
                "& fieldset": { borderColor: "#444" },
                "&:hover fieldset": { borderColor: "#2af598" },
                "&.Mui-focused fieldset": { borderColor: "#2af598" },
              },
              "& .MuiInputLabel-root": { color: "#aaa" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#2af598" },
            }}
            size="small"
          />
          {(dateRangeStart || dateRangeEnd) && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setDateRangeStart("");
                setDateRangeEnd("");
              }}
              sx={{ color: "#fff", borderColor: "#444" }}
            >
              Clear Filter
            </Button>
          )}
        </Box>
      </Box>

      {analysisData && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={dateRangeStart && dateRangeEnd ? 6 : 12}>
              <Alert severity="success">
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Analysis Summary (All Data)
                </Typography>
            <Typography variant="body2">
              Production Total: {analysisData.summary.productionTotal}
              <br />
              Rowy Total: {analysisData.summary.rowyTotal}
              <br />
              Table A (Identical): {analysisData.summary.tableA}
              {analysisData.summary.idDiscrepancies > 0 && (
                <>
                  <br />
                  <Box component="span" sx={{ color: '#ff9800', fontWeight: 'bold' }}>
                    ⚠️ ID Discrepancies: {analysisData.summary.idDiscrepancies} (same data, different IDs)
                  </Box>
                </>
              )}
              <br />
              Table B (Possible Duplicates): {analysisData.summary.tableB}
              <br />
              Table C (Rowy Only): {analysisData.summary.tableC}
              <br />
              Table D (Missing in Rowy): {analysisData.summary.tableD || 0}
            </Typography>
              </Alert>
            </Grid>
            {dateRangeStart && dateRangeEnd && (
              <Grid item xs={12} md={6}>
                <Alert severity="info">
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Range Analysis Summary
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Period: {new Date(dateRangeStart).toLocaleDateString()} - {new Date(dateRangeEnd).toLocaleDateString()}
                  </Typography>
                  {(() => {
                    const rangeSummary = getRangeSummary();
                    if (!rangeSummary) return null;
                    return (
                      <Typography variant="body2">
                        Production Total: {rangeSummary.productionTotal}
                        <br />
                        Rowy Total: {rangeSummary.rowyTotal}
                        <br />
                        Table A (Identical): {rangeSummary.tableA}
                        <br />
                        Table B (Possible Duplicates): {rangeSummary.tableB}
                        <br />
                        Table C (Rowy Only): {rangeSummary.tableC}
                        <br />
                        Table D (Missing in Rowy): {rangeSummary.tableD || 0}
                      </Typography>
                    );
                  })()}
                </Alert>
              </Grid>
            )}
          </Grid>

          {(() => {
            // Filter tables if date range is set
            const tableA = dateRangeStart && dateRangeEnd 
              ? filterByDateRange(analysisData.tableA || [], dateRangeStart, dateRangeEnd)
              : analysisData.tableA || [];
            const tableB = dateRangeStart && dateRangeEnd 
              ? filterByDateRange(analysisData.tableB || [], dateRangeStart, dateRangeEnd)
              : analysisData.tableB || [];
            const tableC = dateRangeStart && dateRangeEnd 
              ? filterByDateRange(analysisData.tableC || [], dateRangeStart, dateRangeEnd)
              : analysisData.tableC || [];
            const tableD = dateRangeStart && dateRangeEnd 
              ? filterByDateRange(analysisData.tableD || [], dateRangeStart, dateRangeEnd)
              : analysisData.tableD || [];
            
            return (
              <>
                {renderTable(tableA, "tableA", "Table A: Identical in Both")}
                {renderTable(tableB, "tableB", "Table B: Possible Duplicates in Rowy")}
                {renderTable(tableC, "tableC", "Table C: Rowy Only")}
                {renderTable(tableD, "tableD", "Table D: Missing in Rowy (Available in Production)")}
              </>
            );
          })()}
        </Box>
      )}
    </Box>
  );
};

export default CollectionCleanUpTool;
